import { useMemo } from "react"
import { Grid, MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { FetchProductsResponse, MerchantProductsQuery, fetchProducts } from "../../products/productsApi"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { ProductOfferItem } from "../offersTypes"

function transformProductsData(data: FetchProductsResponse): ProductOfferItem[] {
    return data.productGroups.flatMap((group) => {
        const products = group.products.map<ProductOfferItem>((product) => ({
            ...product,
        }))

        return products
    })
}

type OffersTabProps = Readonly<{
    priceListId: number
}>

export function OffersTab({ priceListId }: OffersTabProps) {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const products = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: {
            merchantId: merchantId,
            priceListId,
            deliveryDate: dateTimeFactory.toDate(dateTimeFactory.now()),
        },
        select: transformProductsData,
    })
    const { ApiQueryTable, columnFactory } = useApiQueryTable(products)

    const columns = useMemo<MRT_ColumnDef<ProductOfferItem>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("offers-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("offers-table-price-column"),
                    id: "price.amount",
                },
                (row) => row.price,
                true,
            ),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("offers-table-discount-amount-column"),
                    id: "offerDetails.discount.discountAmount.amount",
                },
                (row) => row?.offerDetails?.discount?.discountAmount,
                true,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("offers-table-valid-from-column"),
                    id: "offerDetails.availabilityDetails.dateTimeInterval.from",
                },
                (row) => row?.offerDetails?.availabilityDetails?.dateTimeInterval?.from,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("offers-table-expires-column"),
                    id: "offerDetails.availabilityDetails.dateTimeInterval.to",
                },
                (row) => row?.offerDetails?.availabilityDetails?.dateTimeInterval?.to,
            ),
            columnFactory.activeInactiveStatusColumn(
                { header: l10n.getString("offers-table-status-column"), id: "status" },
                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n],
    )

    return (
        <Grid container direction="column">
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="offers-table-edit-menu-item" key={`edit-${row.original.id}`}>
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                            }}
                        >
                            Edit
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="offers-table-remove-offer-menu-item" key={`remove-offer-${row.original.id}`}>
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                            }}
                        >
                            Remove offer
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
            />
        </Grid>
    )
}
